import React, { useEffect, useRef, useState } from 'react';
import {
	Dimensions,
	View,
	StyleSheet,
	ScrollView,
	Platform,
	Pressable,
	Text,
} from 'react-native';
import FieldBuilder from '../../components/shared/FieldBuilder/FieldBuilder';
import I18n from '../../components/shared/i18n/I18n.component';
import { TimeMode } from '../../components/shared/FieldBuilder/TimePicker/TimePicker.component';
import colors from '../../config/colors';
import OutlineButtonContainer from '../../components/shared/Buttons/OutlineButton.container';
import SolidButtonContainer from '../../components/shared/Buttons/SolidButton.container';
import { TouchableOpacity } from 'react-native';
import { fetchEmployeesFilterList } from '../../store/actions/reportsFilters.actions';
import moment from 'moment';
import useDevicePlatform, {
	DeviceType,
} from '../../hooks/useDevicePlatform.hook';
import { i18nService } from '../../services/i18n.service';
import { Switch } from 'react-native-paper';
import AsyncMultiSelectContainer from '../../components/shared/AsyncMultiSelect/AsyncMultiSelect.container';
import { useWatch } from 'react-hook-form';
import { UPDATE_REASON_OPTIONS } from './AddRequestModal.utils';
import ActionButtonContainer from '../../components/shared/Buttons/ActionButton.container';
import ImageIcon from '../../components/shared/Icon/ImageIcon.component';

interface prevValuesForm {
	employee: {
		id: any;
		name: string;
	};
	projectId: string;
	eventId: string;
	date: string;
	entryTime: string;
	exitTime: string;
	fullDay: boolean;
	employeeId: string;
}

interface Props {
	onSubmit: Function;
	onCancel: Function;
	submitBtnText: string;
	cancelBtnText: string;
	shiftHours?: number;
	prevValues?: prevValuesForm;
	totalReported?: number;
	setIsEventsFieldsOnView: Function;
	isEventsFieldsOnView: boolean;
	erroredFields: any;
	eventsOptions: any[];
	projectsOptions: any[];
	isEdit: boolean;
	isFullDayReport: boolean;
	standardHours: number;
	disableTimePickers: boolean;
	isSubmitButtonDisable: boolean;
	forms: any;
	handleFulldayToggle: Function;
	getErrorLabel: any;
	route: any;
	canReportFullDay: boolean;
	hideName?: boolean;
	addNew?: boolean;
	hideProjects?: boolean;
	hideEvents?: boolean;
	maxDate?: Date;
	businessUnitsList: any[];
	businessUnitId?: number;
	businessUnitName?: string;
}


const selectStyle = {
	wrapper: {
		marginVertical: 10,
		zIndex: 1112,
	},
	control: {
		height: 40,
		maxWidth: '100%',
		width: '100%',
		backgroundColor: colors.white,
		marginBottom: 3,
		borderRadius: 8,
		borderWidth: 1,
		borderColor: '#D1D4DC',									
	},
	placeholder: {
		color: colors.clockRecordBlue,
	},
	menu: {
		borderRadius: 8,
		boxShadow: 'none',
		border: `solid 1px #e7e7e8`,
	},
	option: {
		color: colors.primaryDarkBlue,
		'&:not(:last-of-type)': {
			borderBottom: `1px solid ${colors.datepickerSeparator} `,
		},
		focusedBackgroundColor: colors.iconBG,
	}	
};


const AddRequestComponent = ({
	onSubmit,
	onCancel,
	submitBtnText,
	cancelBtnText,
	prevValues,
	route,
	setIsEventsFieldsOnView,
	isEventsFieldsOnView,
	erroredFields,
	eventsOptions,
	projectsOptions,
	isEdit,
	isFullDayReport,
	standardHours,
	disableTimePickers,
	isSubmitButtonDisable,
	forms,
	handleFulldayToggle,
	getErrorLabel,
	canReportFullDay,
	hideName,
	addNew,
	hideProjects,
	haveEvents,
	formMethods,
	hideEvents,
	onChangeEmployees,
	includeEmployees,
	maxDate,
	businessUnitsList,
	businessUnitId,
	businessUnitName,
}: Props) => {
	const [from, setFrom] = useState(forms.fromDate);
	const [to, setTo] = useState(forms.toDate);

	const platform = useDevicePlatform();
	const isMobile = platform !== DeviceType.WEB;
	const windowHeight = Dimensions.get('window').height;
	const windowWidth = Dimensions.get('window').width;
	const scrollView = useRef<any>();
	const unitId = useWatch({
		name: 'unitId',
	});
	const updateReason = useWatch({
		name: 'updateReason',
	});
	const getEmployeesFilter = async (
		search: string,
		loadedOptions: null,
		{ page }: { page: number },
	) => {
		const res = await fetchEmployeesFilterList(route.name, {
			page,
			pageSize: 20,
			search,
			filter: { dep: [] },
			unitIds: [
				!isEdit ? formMethods.getValues().unitId : businessUnitId,
			],
		});

		return {
			options: (isEventsFieldsOnView && page === 0
				? [
						{
							value: 'all',
							label: i18nService.translate('general.all'),
							textColor: colors.primaryDarkBlue,
							bgColor: colors.white,
							focusedBgColor: colors.iconBG,
							selectedBgColor: colors.selectOptionSelected,
						},
				  ]
				: []
			).concat(
				res?.data?.map((el: any) => {
					return {
						value: el.employeeId,
						label: el.name + ` (${el.teudatZeut})`,
						textColor: el.textColor || colors.primaryDarkBlue,
						bgColor: el.bgColor || colors.white,
						focusedBgColor: el.focusedBgColor || colors.iconBG,
						selectedBgColor:
							el.selectedBgColor || colors.selectOptionSelected,
						extra: { ...el },
					};
				}),
			),
			hasMore: 20 === res?.data?.length,
			additional: {
				page: page + 1,
			},
		};
	};

	const onChangeProjectEvent = (
		getValues: any,
		newValue: string,
		reset: any,
		name: string,
	) => {
		reset(
			{ ...getValues(), [name]: newValue },
			{ keepErrors: true, keepDirty: true },
		);
	};

	useEffect(() => {
		if (getErrorLabel?.props?.errorType) {
			//scroll to the end only if error label exists
			scrollView.current?.scrollToEnd?.({ animated: true });
		}
	}, [getErrorLabel]);

	useEffect(() => {
		hideProjects && setIsEventsFieldsOnView(true);
	}, [hideProjects]);

	const onFormUpdate = (field: string, val: string) => {
		if (field === 'fromDate') {
			setFrom(val);
			if (!to) {
				formMethods.setValue('toDate', val);
				setTo(val);
			}
		} else if (field === 'toDate') {
			setTo(val);
			if (!from) {
				formMethods.setValue('fromDate', val);
				setFrom(val);
			}
		}
	};

	return (
		<View
			style={ isMobile ? {
				paddingVertical: 16,
				paddingHorizontal: 16,
				flexDirection: 'column',
				backgroundColor: colors.white,
				width: '100%',
				flex: 1,
			} : {
				paddingTop: 35,
				paddingBottom: Platform.OS === 'ios' ? 10 : 35,
				paddingHorizontal: 20,
				flexDirection: 'column',
				shadowColor: 'rgba(0, 0, 0, 0)',
				shadowOpacity: 0.1,
				shadowOffset: { width: 0, height: 2 },
				backgroundColor: colors.white,
				borderRadius: 20,
				maxWidth: 408,
				width: 408,
				...Platform.select({
					ios: {
						flex: 1,
						maxHeight: Math.min(windowHeight - 80, 620),
					},
				}),
			}}
		>
			{ !isMobile && (
				<View
					style={{
						flexDirection: 'row-reverse',
						alignItems: 'center',
						justifyContent: 'space-between',
						paddingHorizontal: 0,
						gap: 12
					}}
				>
					<View style={{width: '100%', flexDirection: 'row', alignItems: 'baseline', justifyContent:'space-between'}}>
						<Pressable onPress={onCancel} style={styles.closeButton}>
							<Text style={styles.closeButtonText}>×</Text>
						</Pressable>
						<View style={{ flexDirection: 'row-reverse', justifyContent: 'center', alignItems: 'center', gap: 12, marginBottom: 12}}>
							<I18n
								size={17}
								weight='600'
								color={colors.primaryDarkBlue}
							>
								{isEdit
									? 'addReportModal.updateRequest'
									: 'addReportModal.createNewRequest'}
							</I18n>
							{prevValues?.date && (
								<I18n size={17} weight='400' color={colors.primaryDarkBlue} style={{...Platform.select({ ios: {marginRight: 8}} )}}>
									{moment(prevValues.date, 'YYYY-MM-DD').format('DD/MM/YYYY')}
								</I18n>
							)}
						</View>
						
					</View>
				</View>
				)	
			}
			
			

			{!hideProjects && !hideEvents && (
				<View
					style={{
						flexDirection: 'row',
						justifyContent: 'center',
						marginTop: 10,
						marginBottom: 10,
					}}
				>
					<TouchableOpacity
						style={{
							flexDirection: 'row',
							alignItems: 'center',
							justifyContent: 'center',
							maxWidth: 175,
							flex: 1,
							paddingHorizontal: isMobile ? 10 : 20,
							borderColor: isEventsFieldsOnView ? colors.titlePrimary : colors.clockGreyText,
							borderBottomWidth: 2
						}}
						onPress={() => setIsEventsFieldsOnView(true)}
					>
						<I18n
							style={{ marginVertical: 5 }}
							size={17}
							weight={isEventsFieldsOnView ? 'bold' : 'normal'}
							color={isEventsFieldsOnView ? 'titlePrimary' : 'clockRecordBlue'}
						>
							menu.eventReport
						</I18n>
					</TouchableOpacity>
					<TouchableOpacity
						style={{
							flexDirection: 'row',
							alignItems: 'center',
							justifyContent: 'center',
							maxWidth: 175,
							flex: 1,
							paddingHorizontal: isMobile ? 10 : 20,
							borderColor: !isEventsFieldsOnView ? colors.titlePrimary : colors.clockGreyText,
							borderBottomWidth: 2
						}}
						onPress={() => setIsEventsFieldsOnView(false)}
					>
						<I18n
							style={{ marginVertical: 5 }}
							size={17}
							weight={!isEventsFieldsOnView ? 'bold' : 'normal'}
							color={!isEventsFieldsOnView ? 'titlePrimary' : 'clockRecordBlue'}
						>
							menu.hoursReport
						</I18n>
					</TouchableOpacity>
				</View>
			)}

			
			
			<ScrollView
				style={ isMobile ? {
					flex: 1,
					backgroundColor: colors.white,
					paddingHorizontal: 7,
				} :
					{
					...Platform.select({
						ios: {
							maxHeight: Math.min(windowHeight - 350, 450),
						},
						default: {
							height: windowHeight - 350,
							maxHeight: 450,
						},
					}),
					backgroundColor: colors.white,
					paddingHorizontal: 7,
					}}
				showsVerticalScrollIndicator={true}
				ref={scrollView}
			>
				{!isEdit && businessUnitsList?.length > 1 ? (
					<FieldBuilder
						name='unitId'
						type='reactselect'
						label='general.unitIds'
						key={`${unitId}-unitIds`}
						showFullName
						isRequired
						options={businessUnitsList}
						styles={{
							wrapper: {
								marginVertical: 10,
								zIndex: 11111,
							},
							control: {
								height: 40,
							},
						}}
					/>
				) : businessUnitName ? (
					<View style={{ marginVertical: 10 }}>
						<I18n color='clockRecordBlue' size={12} weight='normal'>
							general.unitIds
						</I18n>
						<I18n color='primaryDarkBlue' size={14 } weight='normal'>
							{businessUnitName}
						</I18n>
					</View>
				) : null}

				{!hideName &&
					(prevValues ? (
						<View style={{ marginVertical: 10 }}>
							<I18n color='clockRecordBlue' size={12} weight='normal'>
								filter.multiselect.workerName
							</I18n>
							<I18n color='primaryDarkBlue' size={14} weight='normal'>
								{prevValues.fullName}
							</I18n>
						</View>
					) : isEventsFieldsOnView ? (
						<FieldBuilder
							styles={{
								wrapper: { marginVertical: 10, zIndex: 1112 },
							}}
							Component={AsyncMultiSelectContainer}
							loadOptions={getEmployeesFilter}
							innerstyles={selectStyle}
							customOnChange={onChangeEmployees}
							key={`${unitId}-${forms.employeeIds}-employeeIdsMoreTOne`}
							type={'customInput'}
							name={'employeeIds'}
							noSelection
							label={() => (
								<I18n size={12} weight='normal' color='clockRecordBlue'>
									filter.multiselect.workerName
								</I18n>
							)}
							error={erroredFields.employeeId}
							getConfig={(restProps: any) => ({
								config: {
									value: restProps.value,
									loadOptions: restProps.loadOptions,
									disabled: restProps.disabled,
									placeholder:
										restProps.value?.length === 0
											? i18nService.translate(
													'addRequestModal.allChosen',
											  )
											: restProps.value?.length &&
											  includeEmployees
											? `${
													restProps.value?.length
											  } ${i18nService.translate(
													'addRequestModal.partialChosen',
											  )}`
											: restProps.value?.length
											? `${i18nService.translate(
													'addRequestModal.allChosenExept',
											  )} ${restProps.value?.length}`
											: i18nService.translate(
													'filter.multiselect.employees',
											  ),
									name: '',
								},
							})}
							specialPlaceholder
							isClearable={false}
							isRequired
							editable
							isDisabled={!unitId}
						/>
					) : (
						<FieldBuilder
							styles={{
								wrapper: {
									marginVertical: 10,
									zIndex: 1112,
								},
								control: {
									height: 40,
									backgroundColor: colors.white,
									borderRadius: 8,
									borderWidth: 1,
									borderColor: '#D1D4DC',									
									},
								placeholder: {
									color: colors.clockRecordBlue,
								},
								menu: {
									borderRadius: 8,
								},
								option: {
									height: 'auto',
									marginTop: 0,
									paddingTop: 8,
									paddingBottom: 8,
									color: colors.primaryDarkBlue,
									'&:not(:last-of-type)': {
										borderBottom: `1px solid ${colors.datepickerSeparator} `,
									},
									focusedBackgroundColor: colors.iconBG,
								}	
							}}
							type={'asyncSelect'}
							key={`${unitId}-${forms.employeeIds}-employeeId`}
							name={'employeeId'}
							label={() => (
								<I18n size={12} weight='normal' color='clockRecordBlue'>
									filter.multiselect.workerName
								</I18n>
							)}
							error={erroredFields.employeeId}
							isRequired={true}
							isDisabled={!unitId}
							options={getEmployeesFilter}
							placeholder={'employees'}
						/>
					))
				}

				{isEventsFieldsOnView ? (
					<View style={{ height: 70, zIndex: 1111 }}>
						<FieldBuilder
							type='reactselect'
							styles={{
									wrapper: { 
										margin: !isMobile ? 0 : undefined,
									},
									control: {
										backgroundColor: colors.white,
										borderRadius: 8,
										borderWidth: 1,
										borderColor: '#D1D4DC',									
									},
									placeholder: {
										color: colors.clockRecordBlue,
									},
									menu: {
										borderRadius: 8,
									},
									option: {
										height: 'auto',
										marginTop: 0,
										paddingBottom: 8,
										color: colors.primaryDarkBlue,
										'&:not(:last-of-type)': {
											borderBottom: `1px solid ${colors.datepickerSeparator} `,
										},
										focusedBackgroundColor: colors.iconBG,
									}								
								}								
							}
							label={() => (
								<I18n size={12} weight='normal' color='clockRecordBlue'>
									menu.event
								</I18n>
							)}
							name='eventId'
							key={`${unitId}-${
								Platform.OS === 'ios' ? '' : forms.eventId
							}-event`}
							isRequired={true}
							editable={true}
							options={eventsOptions}
							error={erroredFields.eventId}
							isSearchable={!isMobile}
							placeholder='chooseEvent'
							onChange={(
								setValue,
								getValues,
								newValue,
								value,
								trigger,
								reset,
							) => {
								onChangeProjectEvent(
									getValues,
									newValue,
									reset,
									'eventId',
								);
							}}
						/>
					</View>
				) : projectsOptions.length ? (
					<View style={{ height: 70, zIndex: 1111 }}>
						<FieldBuilder
							key={`${unitId}-${
								Platform.OS === 'ios' ? '' : forms.projectId
							}-project`}
							error={erroredFields.projectId}
							name='projectId'
							type='reactselect'
							styles={
								!isMobile ? { wrapper: { margin: 0 } } : undefined
							}
							label={() => (
								<I18n size={12} weight='normal' color='clockRecordBlue'>
									filter.multiselect.project
								</I18n>
							)}
							isRequired={true}
							editable={true}
							options={projectsOptions}
							// horizontalPadding={180}
							isSearchable={!isMobile}
							onChange={(
								setValue,
								getValues,
								newValue,
								value,
								trigger,
								reset,
							) => {
								onChangeProjectEvent(
									getValues,
									newValue,
									reset,
									'projectId',
								);
							}}
						/>
					</View>
				) : null}
				
				{isEventsFieldsOnView && addNew && !haveEvents ? (
					<View style={styles.requestDates}>
						<View style={{ width: 140 }}>
							<FieldBuilder
								styles={{ wrapper: { margin: 0 } }}
								type={'datepicker'}
								key={'fromDate'}
								name={'fromDate'}
								isRequired={true}
								inputContainerStyle={{
									padding: '10px 16px',
									width: '100%',
									borderBottom: 'none',
									border: '1px solid #D1D4DC',
									backgroundColor: '#FFF',
									borderRadius: 8,
									alignItems: 'center',
								}}
								popperPlacement='bottom-end'
								popperModifiers={[
									{
										name: 'flip',
										options: {
											fallbackPlacements: ['bottom'],
										},
									},
								]}
								maxDate={
									to
										? new Date(
												moment(
													to,
													'DD/MM/YYYY',
												).valueOf(),
										  )
										: maxDate || undefined
								}
								label={() => (
									<I18n size={12} weight='normal' color='clockRecordBlue'>
										vacationRequest.fromDate
									</I18n>
								)}
								placeholder={'general.datePickerPlaceholder'}
								onChange={(val, a, b) => {
									onFormUpdate(
										'fromDate',
										typeof b === 'string'
											? b
											: b?.target?.value,
									);
								}}
								error={erroredFields.fromDate}
							/>
						</View>
						<View style={{ width: 140 }}>
							<FieldBuilder
								styles={{ wrapper: { margin: 0 } }}
								type={'datepicker'}
								isRequired={true}
								key={'toDate'}
								name={'toDate'}
								inputContainerStyle={{
									padding: '10px 16px',
									borderBottom: 'none',
									border: '1px solid #D1D4DC',
									backgroundColor: '#FFF',
									color: colors.primaryDarkBlue,
									borderRadius: 8,
									alignItems: 'center',
								}}
								popperModifiers={[
									{
										name: 'flip',
										options: {
											fallbackPlacements: ['bottom'],
										},
									},
								]}
								minDate={
									from
										? new Date(
												moment(
													from,
													'DD/MM/YYYY',
												).valueOf(),
										  )
										: undefined
								}
								maxDate={maxDate || undefined}
								label={() => (
									<I18n size={12} weight='normal' color='clockRecordBlue'>
										vacationRequest.toDate
									</I18n>
								)}
								placeholder={'general.datePickerPlaceholder'}
								onChange={(val, a, b) => {
									onFormUpdate(
										'toDate',
										typeof b === 'string'
											? b
											: b?.target?.value,
									);
								}}
								error={erroredFields.toDate}
							/>
						</View>
					</View>
				) : prevValues?.date ? null : (
					<View
						style={{
							flexDirection: 'row-reverse',
							marginBottom: 10,
							zIndex: 111,
							width: '100%',
						}}
					>
						<FieldBuilder
							styles={{
								wrapper: {
									marginVertical: 10,
									width: '100%',
								},
							}}
							key={'date'}
							name={'date'}
							type={'datepicker'}
							inputContainerStyle={{
								padding: '10px 16px',
								width: '100%',
								maxWidth: '100%',
								borderBottom: 'none',
								border: '1px solid #D1D4DC',
								backgroundColor: '#FFF',
								borderRadius: 8,
								alignItems: 'center',
							}}
							popperPlacement='bottom-end'
							popperModifiers={[
								{
									name: 'flip',
									options: {
										fallbackPlacements: ['bottom'],
									},
								},
							]}
							maxDate={maxDate || undefined}
							label={() => (
								<I18n color={colors.primaryDarkBlue} size={12} weight='normal'>
									worklogReportPage.date
								</I18n>
							)}
							placeholder={'general.datePickerPlaceholder'}
							isRequired={true}
							editable={true}
							error={erroredFields.date}
						/>
					</View>
				)}
				{isEventsFieldsOnView && canReportFullDay && from === to ? (
					<View
						style={{
							alignItems: 'flex-end',
							marginBottom: 16,
						}}
					>
						<View
							style={{
								flexDirection: 'row-reverse',
								justifyContent: 'space-between',
								alignItems: 'center',
								width: '100%',
								marginBottom: 5,
							}}
						>
							<I18n size={14} weight='normal' color='clockRecordBlue'>
								modal.report.fullDay
							</I18n>
							<Switch
								color={colors.titlePrimary}
								onValueChange={handleFulldayToggle}
								value={isFullDayReport}
							/>
						</View>
						{(forms.date || (!!from && from === to)) &&
						standardHours ? (
							<I18n
								style={{ textAlign: 'center' }}
								size={12}
								weight='normal'
								color='clockRecordBlue'
							>
								{`${i18nService.translate(
									'modal.AddRequest.fullday.description.entry',
								)} ${
									standardHours
										? standardHours.enterTime
										: ' 08:00 '
								} ${i18nService.translate(
									'modal.AddRequest.fullday.description.exit',
								)} ${
									standardHours
										? standardHours.exitTime
										: ' 08:00 '
								}`}
							</I18n>
						) : null}
					</View>
				) : null}
				{!moment(
					moment(from || forms.date, 'DD/MM/YYYY').format(
						'YYYY-MM-DD',
					),
				).isAfter(moment().format('YYYY-MM-DD')) && (
					<View
						style={{
							zIndex: 1,
						}}
					>
						<FieldBuilder
							disablePicker={
								disableTimePickers || (!!from && from != to)
							}
							key={`${unitId}-entryTime`}
							type={'timePicker'}
							name={'entryTime'}
							defaultTime={
								prevValues ? prevValues.entryTime : null
							}
							styles={{
								wrapper: { flex: 1, ...Platform.select({ ios: { width: '100%' } }) },
							}}
							isRequired={
								!disableTimePickers || !(!!from && from != to)
							}
							mainContainerStyle={{ ...Platform.select({ ios: { width: '100%', alignItems: 'stretch' } }) }}
							wrapperStyle={{  ...Platform.select({ ios: { width: '100%' } }) }}
							containerStyle={{  ...Platform.select({ ios: { width: '100%' } }) }}
							inputContainerStyle={{
								...Platform.select({ ios: { flex: 1 } }),
								height: 40,
								paddingVertical: 10,
								paddingHorizontal: 16,
								justifyContent: 'center',
								alignItems: 'center',
								gap: 10,
								borderRadius: 8,
								borderWidth: 1,
								borderColor: '#D1D4DC',
								backgroundColor: '#FFF',

							}}
								inputStyle={{
								 ...Platform.select({ ios: { flex: 1} }),
								fontFamily: 'RubikRegular',
								fontSize: 14,
								fontWeight: '400',
								fontStyle: 'normal',
								color: colors.primaryDarkBlue,
								width: '100%',
							}}
							editable={true}
							mode={TimeMode.Entry}
							label={() => (
								<I18n size={12} weight='normal' color={colors.primaryDarkBlue}>
									general.enterenceTime
								</I18n>
							)}
							error={erroredFields.entryTime}
						/>
						<FieldBuilder
							disablePicker={
								disableTimePickers || (!!from && from != to)
							}
							key={`${unitId}-exitTime`}
							type={'timePicker'}
							name={'exitTime'}
							defaultTime={
								prevValues ? prevValues.exitTime : null
							}
							styles={{
								wrapper: { flex: 1, ...Platform.select({ ios: { width: '100%' } }) },
							}}
							isRequired={
								// Not required when editing a report with only entry time (no exit yet)
								(isEdit && prevValues?.entryTime && !prevValues?.exitTime)
									? false
									: !disableTimePickers || !(!!from && from != to)
							}
							mainContainerStyle={{ ...Platform.select({ ios: { width: '100%', alignItems: 'stretch' } }) }}
							wrapperStyle={{  ...Platform.select({ ios: { width: '100%' } }) }}
							containerStyle={{  ...Platform.select({ ios: { width: '100%' } }) }}

							inputContainerStyle={{
								...Platform.select({ ios: { flex: 1} }),
								height: 40,
								paddingVertical: 10,
								paddingHorizontal: 16,
								justifyContent: 'center',
								alignItems: 'center',
								gap: 10,
								borderRadius: 8,
								borderWidth: 1,
								borderColor: '#D1D4DC',
								backgroundColor: '#FFF',

							}}
								inputStyle={{
								...Platform.select({ ios: { flex: 1} }),
								fontFamily: 'RubikRegular',
								fontSize: 14,
								fontWeight: '400',
								fontStyle: 'normal',
								color: colors.primaryDarkBlue,
								width: '100%',
							}}
							editable={true}
							mode={TimeMode.Entry}
							label={() => (
								<I18n size={12} weight='normal' color={colors.primaryDarkBlue}>
									general.exitTime
								</I18n>
							)}
							error={erroredFields.exitTime}
						/>
					</View>
				)}
				{isEdit && (
					<View style={{ gap: 10, zIndex: 99}}>
						<View style={{ zIndex: 99}}>
							<FieldBuilder
								type='reactselect'
								name='updateReason'
								label={() => (
								<I18n size={12} weight='normal' color={colors.clockRecordBlue}>
									addReportModal.updateReason
								</I18n>
								)}
								isRequired={true}
								options={UPDATE_REASON_OPTIONS}
								error={erroredFields.updateReason}
								styles={{
									wrapper: { marginVertical: 5, zIndex: 99},
									control: { 
										height: 40,
										paddingHorizontal: 16,
										paddingVertical: 10, 
										justifyContent: 'center',
										alignItems: 'center',
										gap: 10,
										backgroundColor: '#FFF',
										borderColor: '#D1D4DC',
										borderWidth: 1,
										borderRadius: 8,										
									 },
									 menu: {
										width: 356,
										paddingVertical: 8,
										paddingHorizontal: 0,
										backgroundColor: '#FFF',
										borderColor: '#D1D4DC',
										borderWidth: 1,
										borderRadius: 8,
									 },
									 option: {
										cursor: 'pointer',
										paddingHorizontal: 16,
										paddingVertical: 10,
										borderRadius: 8,
										'&:not(:last-of-type)': {
											borderBottom: '1px solid #E2EDF4',
										},
										color: colors.primaryDarkBlue,
										fontSize: 14,
									 },
									 valueContainer :{
										color: colors.primaryDarkBlue,
										fontSize: 14,
									 },
								}}
								onMenuOpen={() => {
									setTimeout(() => {
										scrollView.current?.scrollToEnd?.({ animated: true });
									}, 100);
								}}
								onChange={(setValue, getValues, newValue) => {
									if (newValue !== 'OTHER') {
										setValue('updateReasonText', '');
									}
								}}		
							/>
						</View>
						{updateReason === UPDATE_REASON_OPTIONS.find(option => option.id === 'OTHER')?.id && (
							<View style={{ zIndex: 1 }}>
								<FieldBuilder
									type='input'
									name='updateReasonText'
									label={() => (
									<I18n size={12} weight='normal' color={colors.clockRecordBlue}>
										addReportModal.updateReasonText
									</I18n>
									)}
									isRequired={true}
									maxLength={50}
									error={erroredFields.updateReasonText}
									styles={{
										wrapper: { marginBottom: 50 },
									}}
									style={{
										borderRadius: 8,
										borderWidth: 1,
										borderBottomWidth: 1, //override 
										borderColor: '#D1D4DC',
										backgroundColor: '#FFF',
										paddingVertical: 10,
										paddingHorizontal: 16,
									}}
								/>
							</View>
						)}
					</View>
				)}
				{getErrorLabel}
			</ScrollView>
			<View style={isMobile ? styles.actionButtonsMobile : styles.actionButtons}>
				<ActionButtonContainer
					disabled={isSubmitButtonDisable}
					overrideStyle={isMobile ? styles.submitButtonMobile : styles.updateButton}
					hoverStyle={{backgroundColor: colors.primaryDark }}
					onPress={onSubmit}
				>
					<I18n
						size={15}
						weight='400'
						color='white'
					>
						{submitBtnText}
					</I18n>
				</ActionButtonContainer>
				<ActionButtonContainer
					overrideStyle={isMobile ? styles.cancelButtonMobile : {
						minWidth: 100,
						height: 40,
						justifyContent: 'center',
						paddingHorizontal: 12,
						paddingVertical: 11,
						borderWidth: 1,
						borderColor: colors.titlePrimary,
					}}
					onPress={onCancel}
				>
					<I18n
						size={15}
						//style={{ minHeight: 40 }}
						weight='400'
						color='primary'
					>
						{cancelBtnText}
					</I18n>
				</ActionButtonContainer>
			</View>
		</View>
	);
};
const styles = StyleSheet.create({
	actionButtons: {
		flexDirection: 'row',
		//justifyContent: 'space-evenly',
		alignItems: 'center',
		marginTop: 10,
		gap: 16,
		...Platform.select({
			ios: {
				marginTop: 'auto',
				marginBottom: 24,
			},
		}),
	},
	actionButtonsMobile: {
		flexDirection: 'column',
		alignItems: 'center',
		marginTop: 16,
		gap: 16,
		width: '100%',
	},
	updateButton: {
		width: 100,
		height: 40,
		backgroundColor: colors.titlePrimary,
		borderRadius: 8,
		...Platform.select({
			ios: {
				marginRight: 8,
			},
		}),
	},
	submitButtonMobile: {
		minWidth: '100%',
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
		//paddingHorizontal: 12,
		backgroundColor: colors.titlePrimary,
		borderRadius: 8,
		...Platform.select({
			ios: {
				marginBottom: 8,
			},
		}),
	},
	cancelButtonMobile: {
		minWidth: '100%',
		justifyContent: 'center',
		alignItems: 'center',
		//paddingHorizontal: 12,
		paddingVertical: 11,
		borderWidth: 1,
		borderColor: colors.titlePrimary,
		...Platform.select({
			ios: {
				marginBottom: 8,
			},
		}),
	},
	requestDates: {
		display: 'flex',
		flexDirection: 'row-reverse',
		alignItems: 'center',
		height: 90,
		justifyContent: 'space-between',
		zIndex: 1000,
	},
	closeButton: {
			padding: 4,
			marginLeft: -8,
	},
	closeButtonText: {
		fontSize: 28,
		color: colors.darkGrey,
		lineHeight: 28,
	},
});

export default AddRequestComponent;
